import React, { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import {
  BotMitigationRow,
  EditDomainProductsModalDescription,
  EditDomainProductsModalDomain,
  EditDomainProductsModalProductOption,
  EditDomainProductsModalStyled,
  EditDomainProductsModalWrapper,
} from './EditDomainProductsModal.styled'

import { Domain } from 'Types/Domain'
import {
  UpdateProductParams,
  useEditDomainProducts,
} from 'Features/domainsManagement/api/editDomainProducts'
import {
  Button,
  ButtonWithIcon,
  DesignedModal,
  Toggle,
  useTriggerOnPressEnter,
  CheckBox,
} from '@clickcease/cc-client-components-lib'
import { Diskette } from 'Assets/icons/Diskette'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'

type EditDomainProductsModalProps = {
  domain: Domain
  isOpen: boolean
  onOpenChange: (a: boolean) => void
}

export const EditDomainProductsModal = ({
  domain,
  isOpen,
  onOpenChange,
}: EditDomainProductsModalProps) => {
  const { t } = useTranslation('domainManagement')
  const [paidMarketing, setPaidMarketing] = useState<boolean>(false)
  const [botZapping, setBotZapping] = useState<boolean>(false)
  const [wordpress, setWordpress] = useState<boolean>(false)
  const { data: packagesData } = useGetPackagingLimits()
  const { mutate, isLoading } = useEditDomainProducts()

  useEffect(() => {
    setBotZapping(domain.botZapping)
    setPaidMarketing(domain.paidMarketing)
    setWordpress(!!domain?.wordpress)
  }, [domain?.botZapping, domain?.paidMarketing, domain?.wordpress])

  const translate = (key: string) => t(`edit_domain_products.${key}`)

  const onUpdateSuccess = () => {
    onOpenChange(false)
  }

  const save = () => {
    const params: UpdateProductParams = {
      domainId: domain.id,
      products: {
        paidMarketing,
        botZapping,
        wordpress,
      },
    }

    mutate(params, { onSuccess: onUpdateSuccess })
  }

  useTriggerOnPressEnter(save)

  return (
    <DesignedModal
      title={translate('title')}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      actions={
        <>
          <Button
            variant="ghost"
            text={translate('cancel')}
            onClick={() => {
              onOpenChange(false)
            }}
          />
          <ButtonWithIcon
            iconLeft={<Diskette stroke="#ffffff" fill="rgba(225, 36, 104, 1)" />}
            text={translate('save')}
            loading={isLoading}
            onClick={save}
          />
        </>
      }
    >
      <EditDomainProductsModalStyled>
        <EditDomainProductsModalWrapper>
          <EditDomainProductsModalDescription>
            {translate('select_which')}
          </EditDomainProductsModalDescription>
          <EditDomainProductsModalDomain>{domain.address}</EditDomainProductsModalDomain>
          <EditDomainProductsModalProductOption>
            <Toggle
              value={paidMarketing}
              disabled={!packagesData?.paidMarketingAllowed}
              onChange={() => {
                setPaidMarketing(!paidMarketing)
              }}
            />
            {translate('paid_marketing_protection')}
          </EditDomainProductsModalProductOption>
          <BotMitigationRow>
            {/* Bot Mitigation toggle */}
            <EditDomainProductsModalProductOption>
              <Toggle
                value={botZapping}
                disabled={!packagesData?.botZappingAllowed}
                onChange={() => {
                  setBotZapping(!botZapping)
                }}
              />

              {translate('bot_zapping')}
            </EditDomainProductsModalProductOption>
            {/* WordPress toggle */}
            {packagesData?.botZappingAllowed && (
              <EditDomainProductsModalProductOption>
                <CheckBox
                  isChecked={wordpress}
                  disabled={!botZapping}
                  onChange={(val) => setWordpress(val)}
                  label={translate('is_wordpress')}
                  containerCSS={{ alignItems: 'center' }}
                  labelCSS={{ width: 'auto', marginLeft: '$4' }}
                />
              </EditDomainProductsModalProductOption>
            )}
          </BotMitigationRow>
        </EditDomainProductsModalWrapper>
      </EditDomainProductsModalStyled>
    </DesignedModal>
  )
}
